import React, { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Switch, TextField, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import AddCircleIcon from '@material-ui/icons/AddCircle';

import {
  setFreeTickets,
  setOtherLandingData,
} from '../../../redux/actions/propertyActions';
import { useDataProvider } from 'ra-core';
import { ExtraTickets } from '../../../types/generalSettings';

interface Props {
  price?: number;
  freeTickets?: Array<ExtraTickets>;
  isFreeTicketsActive?: boolean;
  isGeneral?: boolean;
}

interface State {
  isActive: boolean;
  freeTickets: Array<ExtraTickets>;
  docId?: string;
}

const FreeTickets: FC<Props> = ({
  price,
  freeTickets,
  isFreeTicketsActive,
  isGeneral,
}) => {
  const dispatch = useDispatch();
  const dataProvider = useDataProvider();
  const [state, setState] = useState<State>({
    isActive: false,
    freeTickets: Array<ExtraTickets>(),
    docId: '',
  });

  const roundedPrice = (price: number): number => {
    return Math.round((price + Number.EPSILON) * 100) / 100;
  };

  const getGeneral = async () => {
    const result = await dataProvider.getGeneral();
    dispatch(
      setFreeTickets({
        isActive: result.data.isFreeTicketsRates,
        freeTicketsRates: result.data.freeTicketsRates,
      })
    );
    dispatch(
      setOtherLandingData({
        name: result.data.name,
        landingType: result.data.landingType,
        ticketsBundles: result.data.ticketsBundles,
      })
    );
    setState({
      isActive: result.data.isFreeTicketsRates,
      freeTickets: result.data.freeTicketsRates,
    });
  };

  useEffect(() => {
    if (isGeneral) {
      getGeneral();
    }
    if (freeTickets && !isGeneral) {
      dispatch(
        setFreeTickets({
          isActive: isFreeTicketsActive || false,
          freeTicketsRates: freeTickets,
        })
      );
      setState({
        ...state,
        isActive: isFreeTicketsActive || false,
        freeTickets: freeTickets,
      });
    }
  }, []);

  const showNewTicketPrice = (price: number) => {
    if (!price || isNaN(price) || price === Infinity || price === -Infinity) {
      return 0;
    }
    return roundedPrice(price);
  };

  const handleChangeStatus = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    let freeArray = state.freeTickets;
    if (freeArray.length === 0) {
      freeArray = [{ tickets: 3, extra: 1, isShow: true }];
    }
    dispatch(
      setFreeTickets({ freeTicketsRates: freeArray, isActive: checked })
    );
    setState({ ...state, isActive: checked, freeTickets: freeArray });
  };

  const handleTickets = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) => {
    if (parseInt(e.currentTarget.value) < 1) {
      return;
    }

    const freeArray = state.freeTickets;
    freeArray[index].tickets = parseInt(e.currentTarget.value);
    dispatch(
      setFreeTickets({ freeTicketsRates: freeArray, isActive: state.isActive })
    );
    setState({ ...state, freeTickets: freeArray });
  };

  const handleExtraTickets = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) => {
    if (parseInt(e.currentTarget.value) < 0) {
      return;
    }
    const freeArray = state.freeTickets;
    freeArray[index].extra = parseInt(e.currentTarget.value);
    dispatch(
      setFreeTickets({ freeTicketsRates: freeArray, isActive: state.isActive })
    );
    setState({ ...state, freeTickets: freeArray });
  };

  const handleIsShow = (index: number) => {
    const freeArray = state.freeTickets;
    freeArray[index].isShow = !freeArray[index].isShow;
    dispatch(
      setFreeTickets({ freeTicketsRates: freeArray, isActive: state.isActive })
    );
    setState({ ...state, freeTickets: freeArray });
  };

  const removeItem = (index: number) => {
    const freeArray = state.freeTickets;
    let isActive = state.isActive;
    freeArray.splice(index, 1);
    if (freeArray.length === 0) {
      isActive = false;
    }
    dispatch(
      setFreeTickets({ freeTicketsRates: freeArray, isActive: isActive })
    );
    setState({ ...state, freeTickets: freeArray, isActive: isActive });
  };

  const addItem = () => {
    const ticketsCount =
      state.freeTickets.length === 0
        ? 3
        : state.freeTickets[state.freeTickets.length - 1].tickets + 1;
    const freeArray = state.freeTickets;
    const item: ExtraTickets = {
      tickets: ticketsCount,
      extra: 1,
      isShow: true,
    };
    freeArray.push(item);
    dispatch(
      setFreeTickets({ freeTicketsRates: freeArray, isActive: state.isActive })
    );
    setState({ ...state, freeTickets: freeArray });
  };

  return (
    <Box>
      <Box display='flex' alignItems='center'>
        <Typography variant='h6'>Status</Typography>
        <Box marginLeft={1}>
          <Switch
            checked={state.isActive}
            onChange={handleChangeStatus}
            color='primary'
            name='checkedB'
            inputProps={{ 'aria-label': 'primary checkbox' }}
          />
        </Box>
      </Box>
      {state.isActive && (
        <>
          {state.freeTickets.map((item, i) => {
            return (
              <Box
                display='block'
                className='base-color'
                fontFamily='SF UI Display Light'
              >
                <Box display='flex' alignItems='center'>
                  <Box>
                    <Box marginBottom='0.6em'>
                      <TextField
                        type='number'
                        label='count'
                        className='discount-field'
                        value={item.tickets}
                        onChange={(e) => handleTickets(e, i)}
                        defaultValue={1}
                        required
                      />
                    </Box>
                    {!item.tickets && (
                      <Box marginTop='2em' marginLeft='1em'>
                        <div className='common-error'>Required</div>
                      </Box>
                    )}
                    {i !== 0 &&
                      item.tickets <= state.freeTickets[i - 1].tickets && (
                        <Box marginTop='2em' marginLeft='1em'>
                          <div className='common-error'>
                            Should be more than previous value
                          </div>
                        </Box>
                      )}
                    {item.tickets > 1000 && (
                      <Box marginTop='2em' marginLeft='1em'>
                        <div className='common-error'>
                          Should be less than 1000
                        </div>
                      </Box>
                    )}
                  </Box>

                  <Box padding='1em' whiteSpace='nowrap'>
                    bonus is
                  </Box>
                  <Box>
                    <Box marginBottom='0.6em'>
                      <TextField
                        type='number'
                        label='count'
                        className='discount-field'
                        value={item.extra}
                        onChange={(e) => handleExtraTickets(e, i)}
                        defaultValue={1}
                        required
                      />
                    </Box>
                    {}
                    {item.extra < 0 ||
                      (isNaN(item.extra) && (
                        <Box marginTop='2em' marginLeft='1em'>
                          <div className='common-error'>Required</div>
                        </Box>
                      ))}
                    {item.extra > 2000 && (
                      <Box marginTop='2em' marginLeft='1em'>
                        <div className='common-error'>
                          Should be less than 2000
                        </div>
                      </Box>
                    )}
                    {/* {{i !== 0 &&
                      item.extra <= state.freeTickets[i - 1].extra && (
                        <Box marginTop='2em' marginLeft='1em'>
                          <div className='common-error'>
                            Should be more than previous value
                          </div>
                        </Box>
                      )}
                    
                                            checkExtraValue(i, item.tickets, item.extra)
                                        } */}
                  </Box>
                  {price && (
                    <Box padding='1em' whiteSpace='nowrap'>
                      £
                      <span className='discount-is'>
                        new price is{' '}
                        {showNewTicketPrice(
                          (state.freeTickets[i].tickets * price) /
                            (state.freeTickets[i].tickets +
                              state.freeTickets[i].extra)
                        )}
                      </span>
                    </Box>
                  )}
                  <Box margin='0 10px'>
                    <input
                      type='checkbox'
                      checked={item.isShow}
                      onChange={() => handleIsShow(i)}
                      className='input-form'
                    />
                  </Box>
                  <CloseIcon
                    className='remove-discount'
                    onClick={() => removeItem(i)}
                  />
                </Box>
              </Box>
            );
          })}
          <AddCircleIcon onClick={addItem} className='add-discount' />
        </>
      )}
    </Box>
  );
};

export default FreeTickets;
